import { useState } from "react";
import { useLogout, useNotify, useTranslate } from "ra-core";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/supabase/layout";

import {
  FAKE_REST_MFA_ENROLLED_KEY,
  FAKE_REST_MFA_TRUSTED_KEY,
  isDemoMode,
  MFA_SETUP_PATH,
} from "./constants";
import { MfaSmsEnroll } from "./MfaSmsEnroll";
import { MfaTotpEnroll } from "./MfaTotpEnroll";
import { markMfaEnrolled, registerTrustedDevice } from "./trustedDevice";

type MfaMethod = "totp" | "sms";

export const MfaSetupPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const logout = useLogout();
  const [method, setMethod] = useState<MfaMethod | null>(null);
  const [rememberDevice, setRememberDevice] = useState(true);

  const finishSetup = async (factorId: string) => {
    if (isDemoMode()) {
      localStorage.setItem(FAKE_REST_MFA_ENROLLED_KEY, "true");
      if (rememberDevice) {
        localStorage.setItem(FAKE_REST_MFA_TRUSTED_KEY, "true");
      }
      navigate("/");
      return;
    }

    try {
      if (rememberDevice) {
        await registerTrustedDevice({ factorId });
      } else {
        await markMfaEnrolled();
      }
      notify("crm.mfa.setup_complete", {
        type: "success",
        messageArgs: {
          _: "Two-factor authentication is now enabled.",
        },
      });
      navigate("/");
    } catch (error: any) {
      notify(error?.message ?? "crm.mfa.trusted_device_error", {
        type: "error",
        messageArgs: {
          _: "MFA is enabled, but we could not remember this device.",
        },
      });
      navigate("/");
    }
  };

  const handleDemoComplete = () => {
    localStorage.setItem(FAKE_REST_MFA_ENROLLED_KEY, "true");
    if (rememberDevice) {
      localStorage.setItem(FAKE_REST_MFA_TRUSTED_KEY, "true");
    }
    navigate("/");
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {translate("crm.mfa.setup.title", {
              _: "Set up two-factor authentication",
            })}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {translate("crm.mfa.setup.subtitle", {
              _: "Two-factor authentication is required before you can use Gobo CRM.",
            })}
          </p>
        </div>

        {isDemoMode() ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {translate("crm.mfa.demo.stub", {
                _: "Demo mode: MFA is simulated. Click continue to proceed.",
              })}
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(event) => setRememberDevice(event.target.checked)}
              />
              {translate("crm.mfa.remember_device", {
                _: "Remember this device for 30 days",
              })}
            </label>
            <Button type="button" className="w-full" onClick={handleDemoComplete}>
              {translate("crm.mfa.demo.continue", { _: "Continue" })}
            </Button>
          </div>
        ) : !method ? (
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start"
              onClick={() => setMethod("totp")}
            >
              {translate("crm.mfa.setup.choose_totp", {
                _: "Authenticator app (recommended)",
              })}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start"
              onClick={() => setMethod("sms")}
            >
              {translate("crm.mfa.setup.choose_sms", { _: "SMS text message" })}
            </Button>
            <p className="text-sm text-muted-foreground">
              {translate("crm.mfa.setup.backup_hint", {
                _: "Tip: Add a backup authenticator app in your profile so you are not locked out.",
              })}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {method === "totp" ? (
              <MfaTotpEnroll onEnrolled={finishSetup} />
            ) : (
              <MfaSmsEnroll onEnrolled={finishSetup} />
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(event) => setRememberDevice(event.target.checked)}
              />
              {translate("crm.mfa.remember_device", {
                _: "Remember this device for 30 days",
              })}
            </label>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => setMethod(null)}
            >
              {translate("crm.mfa.back", { _: "Back" })}
            </Button>
          </div>
        )}

        <Button
          type="button"
          variant="link"
          className="w-full"
          onClick={() => logout()}
        >
          {translate("ra.auth.logout", { _: "Log out" })}
        </Button>
      </div>
    </Layout>
  );
};

MfaSetupPage.path = MFA_SETUP_PATH.slice(1);
